import { Button, DatePicker, Drawer, Select } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DATE_BACKEND_FORMAT } from '../../../constants';
import { getDrivers } from '../../../modules/drivers/api';
import { getPartners } from '../../../modules/partners/api';

const { RangePicker } = DatePicker;

const RoutesFilter = ({ open, onClose, query, onApply, hidePartner, hideDriver }) => {
  const { t } = useTranslation('routes');
  const { t: tc } = useTranslation('common');

  const ROUTE_STATUSES = Object.keys(t('routeStatuses', { returnObjects: true })).map((key) => ({
    value: key,
    label: t(`routeStatuses.${key}`),
  }));

  const [driverOptions, setDriverOptions] = useState([]);
  const [partnerOptions, setPartnerOptions] = useState([]);

  const [dateRange, setDateRange] = useState(null);
  const [driver, setDriver] = useState(undefined);
  const [status, setStatus] = useState(undefined);
  const [partnerId, setPartnerId] = useState(undefined);

  // #159 — the partner portal (`hideDriver`) must not load /drivers (OVO-002);
  // its routes list doesn't filter by driver anyway.
  useEffect(() => {
    if (hideDriver) return;
    const loadDrivers = async () => {
      const { response } = await getDrivers({ take: 1000 });
      if (response?.drivers) {
        const sorted = [...response.drivers].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
        );
        setDriverOptions(sorted.map((d) => ({ label: d.name, value: d.name })));
      }
    };
    loadDrivers();
  }, [hideDriver]);

  useEffect(() => {
    if (hidePartner) return;
    const loadPartners = async () => {
      const { response } = await getPartners({ take: 1000 });
      if (response?.partners) {
        const sorted = [...response.partners].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
        );
        setPartnerOptions(sorted.map((p) => ({ label: p.name, value: String(p.id) })));
      }
    };
    loadPartners();
  }, [hidePartner]);

  useEffect(() => {
    if (!open) return;
    setDateRange(
      query.dateFrom || query.dateTo
        ? [
            query.dateFrom ? dayjs(query.dateFrom, DATE_BACKEND_FORMAT) : null,
            query.dateTo ? dayjs(query.dateTo, DATE_BACKEND_FORMAT) : null,
          ]
        : null,
    );
    setDriver(query.driver || undefined);
    setStatus(query.status || undefined);
    setPartnerId(query.partnerId || undefined);
  }, [open, query]);

  const handleApply = () => {
    const params = { ...query };
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.dateFrom = dateRange[0].format(DATE_BACKEND_FORMAT);
      params.dateTo = dateRange[1].format(DATE_BACKEND_FORMAT);
    } else {
      delete params.dateFrom;
      delete params.dateTo;
    }
    if (driver) params.driver = driver;
    else delete params.driver;
    if (status) params.status = status;
    else delete params.status;
    if (!hidePartner) {
      if (partnerId) params.partnerId = partnerId;
      else delete params.partnerId;
    }
    params.current = 1;
    onApply(params, true);
    onClose();
  };

  const handleClear = () => {
    setDateRange(null);
    setDriver(undefined);
    setStatus(undefined);
    setPartnerId(undefined);
    const params = { ...query };
    delete params.dateFrom;
    delete params.dateTo;
    delete params.driver;
    delete params.status;
    if (!hidePartner) delete params.partnerId;
    params.current = 1;
    onApply(params, true);
    onClose();
  };

  const filterOption = (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  return (
    <Drawer
      title={t('filterRoutes')}
      placement="right"
      onClose={onClose}
      open={open}
      zIndex={1010}
      width={360}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleClear}>{tc('clear')}</Button>
          <Button type="primary" onClick={handleApply}>
            {tc('apply')}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('dateRange')}</label>
          <RangePicker
            style={{ width: '100%' }}
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format={DATE_BACKEND_FORMAT}
          />
        </div>
        {!hideDriver && (
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('driver')}</label>
            <Select
              style={{ width: '100%' }}
              placeholder={t('driverName')}
              value={driver}
              onChange={(val) => setDriver(val)}
              allowClear
              showSearch
              filterOption={filterOption}
              options={driverOptions}
            />
          </div>
        )}
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('status')}</label>
          <Select
            style={{ width: '100%' }}
            placeholder={t('allStatuses')}
            value={status}
            onChange={(val) => setStatus(val)}
            allowClear
            options={ROUTE_STATUSES}
          />
        </div>
        {!hidePartner && (
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('partners')}</label>
            <Select
              style={{ width: '100%' }}
              placeholder={t('allPartners')}
              value={partnerId}
              onChange={(val) => setPartnerId(val)}
              allowClear
              showSearch
              filterOption={filterOption}
              options={partnerOptions}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default RoutesFilter;
